// Single source of truth for what the declaration upload accepts, mirroring the
// backend's one detection helper (`mc_tools/declaration_format.py`): a PDF, or the
// modern Office workbook family the reviewer's viewer can actually render. Shared by
// every entry point (file-picker input, in-dialog drag-and-drop, the page-level
// DropOverlay) so they can never drift out of sync — and there is exactly ONE gate
// here, `isAcceptedDeclarationFile`, which every path calls.
//
// This gate is ADVISORY and name-based: the backend decides from the bytes. It exists
// only to spare an obvious mistake a round-trip, and its refusal names what is
// supported — so a correctly-formatted file carrying the wrong extension is refused
// with an instruction rather than silently.
export const DECLARATION_ACCEPTED_EXTENSIONS = [".pdf", ".xlsx", ".xlsm"]
// The workbook subset of the above — kept as a filter rather than a second
// hand-typed list, so a future accepted workbook extension can never name
// itself here without also being accepted for upload.
export const DECLARATION_WORKBOOK_EXTENSIONS =
  DECLARATION_ACCEPTED_EXTENSIONS.filter((ext) => ext !== ".pdf")
export const DECLARATION_FILE_INPUT_ACCEPT = [
  ".pdf",
  ".xlsx",
  ".xlsm",
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel.sheet.macroEnabled.12",
].join(",")

function extensionOfName(name: string): string {
  return "." + (name.split(".").pop()?.toLowerCase() ?? "")
}

function extensionOf(file: File): string {
  return extensionOfName(file.name)
}

export function isAcceptedDeclarationFile(file: File): boolean {
  return DECLARATION_ACCEPTED_EXTENSIONS.includes(extensionOf(file))
}

/**
 * Is a declaration's file name a workbook (`.xlsx`/`.xlsm`), by extension
 * alone — no bytes, no extraction run, no read model involved. This is the
 * ONE signal the Studio V2 document pane's viewer dispatch should trust: the
 * file itself never changes shape across extraction states (pending,
 * processing, re-evaluating, failed), unlike the extraction read model's own
 * `document_kind`, which is simply absent until a run has completed at least
 * once (see `StudioV2Layout`'s `isWorkbook`).
 */
export function isWorkbookFileName(
  fileName: string | null | undefined,
): boolean {
  if (!fileName) return false
  return DECLARATION_WORKBOOK_EXTENSIONS.includes(extensionOfName(fileName))
}
